/**
 * SportsFuture wallet button — OpenPlaza ANSWalletButton layout without
 * music, KeepCell, or pending-offers (those stay on OpenPlaza).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { toast } from 'react-toastify';
import {
  Wallet,
  Copy,
  LogOut,
  ChevronDown,
  Loader2,
  Star,
  RefreshCw,
  Eye,
  EyeOff,
  ExternalLink,
} from 'lucide-react';
import { ANS_CONFIG } from '../../config/ansConfig';
import { useWalletDiscovery } from '../../hooks/useWalletDiscovery';
import { useUnifiedWallet } from '../../hooks/useUnifiedWallet';
import { useWalletModal } from '../../hooks/useWalletModal';
import type { DiscoveredWallet } from '../../hooks/useWalletDiscovery';
import { getTheme, type ColorTheme } from '../../lib/themes';
import {
  APERTUM_RPC,
  SFUT_TOKEN,
  WAPTM_TOKEN,
  explorerAddressUrl,
  formatAddress,
} from '../../web3/apertumChain';
import { needsDidOnboarding } from '../../web3/didEligibility';
import { publicIpfsMediaUrl } from '../../web3/ipfs';
import { useWeb3Auth } from '../../web3/Web3AuthContext';
import GetDidCta from '../GetDidCta';
import WalletSelectionModal from './WalletSelectionModal';

const ERC20_BALANCE_ABI = ['function balanceOf(address owner) view returns (uint256)'];

const REVERSE_REGISTRAR_ABI = [
  'function getPrimaryDomain(address addr) external view returns (bytes32)',
  'event PrimaryDomainSet(address indexed owner, bytes32 indexed node, string domainName)',
];
const PUBLIC_RESOLVER_ABI = [
  'function text(bytes32 node, string key) external view returns (string)',
];
const ANS_REGISTRY_ABI = [
  'function resolver(bytes32 node) external view returns (address)',
];

let readOnlyRpc: ethers.providers.JsonRpcProvider | null = null;
function getReadRpc() {
  if (!readOnlyRpc) readOnlyRpc = new ethers.providers.JsonRpcProvider(APERTUM_RPC);
  return readOnlyRpc;
}

type Props = {
  className?: string;
  onSignedIn?: () => void;
};

type WalletUiState = {
  balance: string | null;
  wAptmBalance: string | null;
  sfutBalance: string | null;
  primaryDomain: string | null;
  primaryDomainNode: string | null;
  primaryDomainResolver: string | null;
  avatar: string | null;
  domainTheme: ColorTheme | null;
};

const emptyUi = (): WalletUiState => ({
  balance: null,
  wAptmBalance: null,
  sfutBalance: null,
  primaryDomain: null,
  primaryDomainNode: null,
  primaryDomainResolver: null,
  avatar: null,
  domainTheme: null,
});

function shortAddress(address: string): string {
  return address.slice(0, 6);
}

function addOpacity(hex: string, opacity: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

function avatarDisplayUrl(avatar: string | null, identifier: string): string {
  if (!avatar) {
    return `https://api.dicebear.com/7.x/identicon/svg?seed=${encodeURIComponent(identifier)}`;
  }
  if (avatar.startsWith('data:')) return avatar;
  if (
    avatar.startsWith('ipfs://') ||
    /\/ipfs\//.test(avatar) ||
    /^(bafy|Qm)[a-zA-Z0-9]+$/.test(avatar)
  ) {
    try {
      return publicIpfsMediaUrl(avatar);
    } catch {
      /* fall through */
    }
  }
  if (avatar.startsWith('http') || avatar.startsWith('/')) return avatar;
  return `https://api.dicebear.com/7.x/identicon/svg?seed=${encodeURIComponent(identifier)}`;
}

async function fetchAllBalances(address: string) {
  const rpc = getReadRpc();
  const wAptm = new ethers.Contract(WAPTM_TOKEN, ERC20_BALANCE_ABI, rpc);
  const sfut = new ethers.Contract(SFUT_TOKEN, ERC20_BALANCE_ABI, rpc);
  const [nativeBal, wAptmBal, sfutBal] = await Promise.all([
    rpc.getBalance(address),
    wAptm.balanceOf(address),
    sfut.balanceOf(address),
  ]);
  return {
    balance: ethers.utils.formatEther(nativeBal),
    wAptmBalance: ethers.utils.formatEther(wAptmBal),
    sfutBalance: ethers.utils.formatEther(sfutBal),
  };
}

const ConnectWalletButton = ({ className = '', onSignedIn }: Props) => {
  const auth = useWeb3Auth();
  const discovery = useWalletDiscovery();
  const unified = useUnifiedWallet();
  const walletModal = useWalletModal();
  const [connectingWalletId, setConnectingWalletId] = useState<string | null>(null);
  const [ui, setUi] = useState<WalletUiState>(emptyUi);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFetchingDomain, setIsFetchingDomain] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showWalletInfo, setShowWalletInfo] = useState(true);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const contracts = ANS_CONFIG.contracts;
  const hasDomainTheme = ui.domainTheme !== null;
  const domainTheme = ui.domainTheme;

  const fetchWalletExtras = useCallback(async (address: string) => {
    try {
      const balances = await fetchAllBalances(address);
      setUi((prev) => ({ ...prev, ...balances }));
    } catch (err) {
      console.error('[ConnectWalletButton] balance fetch failed', err);
    }

    setIsFetchingDomain(true);
    try {
      const rpc = getReadRpc();
      const reverseRegistrar = new ethers.Contract(
        contracts.reverseRegistrar,
        REVERSE_REGISTRAR_ABI,
        rpc
      );
      const ansRegistry = new ethers.Contract(
        contracts.ansRegistry,
        ANS_REGISTRY_ABI,
        rpc
      );
      const primaryNode = await reverseRegistrar.getPrimaryDomain(address);
      if (!primaryNode || primaryNode === ethers.constants.HashZero) {
        setUi((prev) => ({
          ...prev,
          primaryDomain: null,
          primaryDomainNode: null,
          primaryDomainResolver: null,
          avatar: null,
          domainTheme: null,
        }));
        return;
      }
      const resolverAddress = await ansRegistry.resolver(primaryNode);
      if (!resolverAddress || resolverAddress === ethers.constants.AddressZero) {
        setUi((prev) => ({
          ...prev,
          primaryDomain: null,
          primaryDomainNode: primaryNode,
          primaryDomainResolver: null,
          avatar: null,
          domainTheme: null,
        }));
        return;
      }
      const resolver = new ethers.Contract(
        resolverAddress,
        PUBLIC_RESOLVER_ABI,
        rpc
      );
      const [avatar, themeKey] = await Promise.all([
        resolver.text(primaryNode, 'avatar').catch(() => ''),
        resolver.text(primaryNode, 'ans.theme').catch(() => ''),
      ]);
      let domainName: string | null = null;
      try {
        const filter = reverseRegistrar.filters.PrimaryDomainSet(address);
        const events = await reverseRegistrar.queryFilter(filter, -100000);
        for (let i = events.length - 1; i >= 0; i--) {
          const event = events[i];
          if (event.args && event.args.node === primaryNode) {
            domainName = event.args.domainName as string;
            break;
          }
        }
      } catch {
        /* domain name is optional */
      }
      setUi((prev) => ({
        ...prev,
        primaryDomain: domainName,
        primaryDomainNode: primaryNode,
        primaryDomainResolver: resolverAddress,
        avatar: avatar || null,
        domainTheme: themeKey ? getTheme(themeKey) : null,
      }));
    } catch (err) {
      console.error('[ConnectWalletButton] ANS fetch failed', err);
    } finally {
      setIsFetchingDomain(false);
    }
  }, [contracts.ansRegistry, contracts.reverseRegistrar]);

  useEffect(() => {
    if (!unified.address || !unified.isConnected) {
      setUi(emptyUi());
      return;
    }
    void fetchWalletExtras(unified.address);
  }, [unified.address, unified.isConnected, fetchWalletExtras]);

  useEffect(() => {
    if (!unified.isConnected || !unified.address) return;
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible' || !unified.address) return;
      void fetchAllBalances(unified.address)
        .then((balances) => setUi((prev) => ({ ...prev, ...balances })))
        .catch(() => undefined);
    }, 15000);
    return () => window.clearInterval(id);
  }, [unified.isConnected, unified.address]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const handleWalletSelected = async (wallet: DiscoveredWallet) => {
    setConnectingWalletId(wallet.id);
    // QR / AppKit must not sit under the picker (w3m-modal z-index < z-40010).
    if (wallet.type === 'walletconnect') {
      walletModal.close();
    }
    try {
      await unified.connect(wallet);
      walletModal.close();
    } catch {
      walletModal.close();
    } finally {
      setConnectingWalletId(null);
    }
  };

  const handleManualRefresh = async () => {
    if (!unified.address || isManualRefreshing) return;
    setIsManualRefreshing(true);
    try {
      const balances = await fetchAllBalances(unified.address);
      setUi((prev) => ({ ...prev, ...balances }));
    } catch {
      toast.error('Failed to refresh balance');
    } finally {
      setTimeout(() => setIsManualRefreshing(false), 400);
    }
  };

  const copyAddress = async () => {
    if (!unified.address) return;
    try {
      await navigator.clipboard.writeText(unified.address);
      setCopied(true);
      toast.success('Address copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleDisconnect = async () => {
    setIsDropdownOpen(false);
    setUi(emptyUi());
    await auth.signOut();
  };

  const handlePrimary = async () => {
    try {
      if (!unified.isConnected) {
        walletModal.open();
        return;
      }
      if (!unified.isCorrectChain) {
        await unified.switchToApertum();
        return;
      }
      if (auth.loginStep === 'needs_signature') {
        await auth.signIn();
        onSignedIn?.();
        return;
      }
      setIsDropdownOpen((v) => !v);
      if (!isDropdownOpen && unified.address) {
        void fetchAllBalances(unified.address)
          .then((balances) => setUi((prev) => ({ ...prev, ...balances })))
          .catch(() => undefined);
      }
    } catch {
      /* auth.error / toast already set */
    }
  };

  const busy = unified.isConnecting || auth.isSigningIn;
  const adminPending =
    auth.eligibility?.reason === 'admin_bypass' && !auth.isAuthenticated;

  const displayName = unified.isConnected
    ? isFetchingDomain
      ? 'Loading...'
      : ui.primaryDomain
        ? ui.primaryDomain
        : showWalletInfo
          ? formatAddress(unified.address || '')
          : '••••••'
    : '';

  const hasPrimaryDomain = Boolean(ui.primaryDomainNode && ui.primaryDomainResolver && !isFetchingDomain);
  const avatarIdentifier = ui.primaryDomain || ui.primaryDomainNode || unified.address || '';
  const displayAvatar =
    unified.isConnected && hasPrimaryDomain
      ? avatarDisplayUrl(ui.avatar, avatarIdentifier)
      : null;

  const domainStyles =
    hasDomainTheme && domainTheme
      ? {
          button: {
            backgroundColor: domainTheme.backgroundColorHex || 'rgba(26, 32, 44, 0.95)',
            borderColor: addOpacity(domainTheme.primaryColorHex || '#007BFF', 0.3),
            borderRadius: '0.75rem',
          },
          dropdown: {
            backgroundColor: domainTheme.backgroundColorHex || '#ffffff',
            borderColor: addOpacity(domainTheme.primaryColorHex || '#007BFF', 0.3),
            borderRadius: '0.75rem',
          },
          primaryColor: domainTheme.primaryColorHex || '#007BFF',
          textColor: domainTheme.textColorHex || '#ffffff',
        }
      : null;

  const connectLabel = busy ? 'Connecting…' : 'Connect Wallet';
  const signatureLabel = busy
    ? adminPending
      ? 'Signing in as Admin…'
      : 'Waiting for signature…'
    : adminPending
      ? 'Sign in as Admin'
      : 'Sign to continue';

  return (
    <>
      {!unified.isConnected && (
        <button
          type="button"
          onClick={handlePrimary}
          disabled={busy}
          className={`inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-70 sm:gap-2 sm:px-4 ${className}`.trim()}
          aria-label="Connect wallet"
          data-sf="wallet-connect"
        >
          {busy ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Wallet className="h-4 w-4 shrink-0" />}
          <span className="truncate sm:hidden">{busy ? 'Connecting…' : 'Connect'}</span>
          <span className="hidden truncate sm:inline">{connectLabel}</span>
        </button>
      )}

      {unified.isConnected && !unified.isCorrectChain && (
        <button
          type="button"
          onClick={handlePrimary}
          disabled={busy}
          className={`inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-70 sm:gap-2 sm:px-4 ${className}`.trim()}
        >
          <Wallet size={16} className="shrink-0" />
          <span className="sm:hidden">Switch</span>
          <span className="hidden sm:inline">Switch to Apertum</span>
        </button>
      )}

      {unified.isConnected && unified.isCorrectChain && auth.loginStep === 'needs_signature' && (
        <button
          type="button"
          onClick={handlePrimary}
          disabled={busy}
          className={`inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-70 sm:gap-2 sm:px-4 ${className}`.trim()}
        >
          {busy ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Wallet size={16} className="shrink-0" />}
          <span className="truncate">{signatureLabel}</span>
        </button>
      )}

      {unified.isConnected && unified.isCorrectChain && auth.loginStep !== 'needs_signature' && (
        <div className="relative min-w-0 max-w-full" ref={dropdownRef} data-sf="wallet-connected">
          <button
            type="button"
            onClick={handlePrimary}
            aria-expanded={isDropdownOpen}
            aria-haspopup="menu"
            aria-label="Wallet menu"
            className={
              hasDomainTheme
                ? `flex min-w-0 max-w-[min(9rem,calc(100vw-6rem))] items-center gap-1.5 border px-2 py-1.5 backdrop-blur-lg transition hover:opacity-90 sm:max-w-[12rem] sm:gap-2 sm:px-3 lg:max-w-full ${className}`
                : `inline-flex min-w-0 max-w-[min(9rem,calc(100vw-6rem))] items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-2 py-1.5 text-sm font-semibold text-slate-800 hover:border-primary sm:max-w-[12rem] sm:gap-2 sm:px-3 lg:max-w-full ${className}`
            }
            style={domainStyles?.button}
            title={
              ui.balance && showWalletInfo
                ? `${displayName || 'Wallet'} · ${parseFloat(ui.balance).toFixed(4)} APTM`
                : displayName || 'Wallet'
            }
          >
            {isFetchingDomain ? (
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-primary to-primary-400">
                <Loader2 className="h-4 w-4 animate-spin text-white" />
              </div>
            ) : hasPrimaryDomain && displayAvatar ? (
              <img
                src={displayAvatar}
                alt={ui.primaryDomain || 'Domain'}
                className="h-7 w-7 flex-shrink-0 rounded-full border-2 border-primary object-cover"
                style={hasDomainTheme ? { borderColor: domainStyles?.primaryColor } : undefined}
              />
            ) : (
              <div
                className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-primary to-primary-400"
                style={hasDomainTheme ? { backgroundColor: domainStyles?.primaryColor } : undefined}
              >
                <Wallet className="h-4 w-4 text-white" />
              </div>
            )}

            <div className="hidden min-w-0 flex-1 overflow-hidden text-left min-[400px]:block">
              <div className="flex items-center gap-1 truncate font-mono text-[11px] font-semibold sm:text-sm">
                {isFetchingDomain || ui.primaryDomain || !showWalletInfo || !unified.address ? (
                  <span className="truncate">{displayName}</span>
                ) : (
                  <>
                    <span className="truncate 2xl:hidden">{shortAddress(unified.address)}</span>
                    <span className="hidden truncate 2xl:inline">
                      {formatAddress(unified.address)}
                    </span>
                  </>
                )}
                {hasPrimaryDomain && (
                  <span className="hidden flex-shrink-0 rounded bg-primary-50 px-1.5 py-0.5 text-xs text-primary lg:inline">
                    Primary
                  </span>
                )}
                {auth.isAuthenticated && auth.isAdmin && (
                  <span className="hidden flex-shrink-0 text-xs font-medium text-primary sm:inline">
                    Admin
                  </span>
                )}
              </div>
              {ui.balance && (
                <span className="truncate text-[10px] text-primary sm:text-xs">
                  {showWalletInfo ? `${parseFloat(ui.balance).toFixed(2)} APTM` : '•••• APTM'}
                </span>
              )}
            </div>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-slate-400 transition ${isDropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {isDropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-[55] bg-black/40 sm:bg-transparent"
                onClick={() => setIsDropdownOpen(false)}
                aria-hidden
              />
              <div
                role="menu"
                data-sf="wallet-dropdown"
                className="fixed z-[60] left-3 right-3 top-[6.5rem] max-h-[min(70dvh,calc(100dvh-7rem))] w-auto overflow-y-auto rounded-2xl border border-slate-100 bg-white/95 p-3 shadow-xl sm:absolute sm:inset-auto sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 sm:max-w-[min(20rem,calc(100vw-1.5rem))]"
                style={domainStyles?.dropdown}
              >
                {ui.primaryDomain && (
                  <div className="mb-4 border-b border-slate-100 pb-4">
                    <div className="mb-1 flex items-center gap-1 text-xs text-slate-500">
                      <Star className="h-3 w-3 fill-current text-primary" />
                      Primary Domain
                    </div>
                    <div className="truncate text-base font-bold text-primary">
                      {ui.primaryDomain}
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <div className="mb-1 flex items-center justify-between text-xs font-medium text-slate-500">
                    <span>Wallet Address</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        title={showWalletInfo ? 'Hide wallet info' : 'Show wallet info'}
                        className="rounded p-1 hover:bg-slate-100"
                        onClick={() => setShowWalletInfo((v) => !v)}
                      >
                        {showWalletInfo ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        type="button"
                        aria-label="Copy address"
                        className="rounded p-1 hover:bg-slate-100"
                        onClick={() => void copyAddress()}
                      >
                        {copied ? <Star className="h-4 w-4 text-primary" /> : <Copy size={14} />}
                      </button>
                    </div>
                  </div>
                  <p className="font-mono text-sm text-slate-800">
                    {showWalletInfo ? formatAddress(unified.address || '') : '••••••••••••'}
                  </p>
                </div>

                <div className="mb-3 space-y-1.5">
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">APTM Balance</span>
                      <button
                        type="button"
                        onClick={() => void handleManualRefresh()}
                        disabled={isManualRefreshing}
                        className="rounded p-0.5 hover:bg-slate-100"
                        title="Refresh balances"
                      >
                        <RefreshCw
                          className={`h-3 w-3 text-slate-400 ${isManualRefreshing ? 'animate-spin' : ''}`}
                        />
                      </button>
                    </div>
                    <span className="truncate font-semibold text-slate-800">
                      {showWalletInfo
                        ? `${parseFloat(ui.balance ?? '0').toFixed(4)} APTM`
                        : '•••• APTM'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-slate-500">wAPTM Balance</span>
                    <span className="truncate font-semibold text-slate-800">
                      {showWalletInfo
                        ? `${parseFloat(ui.wAptmBalance ?? '0').toFixed(4)} wAPTM`
                        : '•••• wAPTM'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-slate-500">SFUT Balance</span>
                    <span className="truncate font-semibold text-slate-800">
                      {showWalletInfo
                        ? `${parseFloat(ui.sfutBalance ?? '0').toFixed(4)} SFUT`
                        : '•••• SFUT'}
                    </span>
                  </div>
                </div>

                <div className="mb-3 flex items-center justify-between text-sm">
                  <span className="text-slate-500">Network</span>
                  <span className={unified.isCorrectChain ? 'text-green-700' : 'text-red-600'}>
                    {unified.isCorrectChain ? 'Apertum' : 'Wrong network'}
                  </span>
                </div>

                {auth.isAdmin && (
                  <div className="mb-3 flex justify-between text-sm">
                    <span className="text-slate-500">Role</span>
                    <span className="font-semibold text-slate-800">Admin</span>
                  </div>
                )}

                {((auth.claim && auth.claim.didId > 0) ||
                  (auth.eligibility?.didId && auth.eligibility.didId > 0)) && (
                  <div className="mb-3 flex justify-between text-sm">
                    <span className="text-slate-500">Global DID</span>
                    <span className="font-semibold text-slate-800">
                      #{auth.claim?.didId || auth.eligibility?.didId}
                    </span>
                  </div>
                )}

                {auth.eligibility &&
                  !auth.eligibility.eligible &&
                  !auth.isAdmin &&
                  needsDidOnboarding(auth.eligibility.reason) && (
                    <div className="mb-3">
                      <GetDidCta
                        compact
                        reason={auth.eligibility.reason}
                        message={auth.eligibility.message}
                      />
                    </div>
                  )}
                {auth.eligibility &&
                  !auth.eligibility.eligible &&
                  !auth.isAdmin &&
                  !needsDidOnboarding(auth.eligibility.reason) && (
                    <p className="mb-3 text-sm text-red-600">{auth.eligibility.message}</p>
                  )}

                <a
                  href={unified.address ? explorerAddressUrl(unified.address) : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <ExternalLink size={14} />
                  View on Explorer
                </a>
                <button
                  type="button"
                  onClick={() => void handleDisconnect()}
                  className="mt-1 flex w-full items-center gap-2 rounded-lg border-t border-slate-100 px-2 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut size={14} />
                  Disconnect
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {walletModal.isOwner && (
        <WalletSelectionModal
          isOpen={walletModal.isOpen}
          onClose={walletModal.close}
          wallets={discovery.wallets}
          isDiscovering={discovery.isDiscovering}
          onSelectWallet={(wallet) => void handleWalletSelected(wallet)}
          isConnecting={unified.isConnecting}
          connectingWalletId={connectingWalletId}
          mobileWalletInfo={discovery.mobileWalletInfo}
        />
      )}
    </>
  );
};

export default ConnectWalletButton;
